/**
 * useAutoFitColumns — «best fit»-kolonnebredder for WarehouseTable (TW 36396506).
 *
 * Kolonner brukeren ikke har dratt selv (userWidth !== true) får bredde etter innholdet
 * (overskrift + data på gjeldende side):
 *  1. Tabellen legges midlertidig ut med table-layout:auto og uten px-bredder, og
 *     naturlig bredde leses av per kolonne (header-cellen = kolonnens bredde i auto-layout).
 *  2. Alt gjenopprettes i samme layout-effekt — ingen paint imellom, så ingen flimring.
 *  3. Plassen som er igjen i containeren gis KUN til tekstkolonner. Tall-, dato- og
 *     statuskolonner holdes på naturlig bredde. Blir summen bredere enn containeren,
 *     scroller tabellen horisontalt — innhold kuttes ikke.
 *
 * Måling kjøres bare når synlige kolonner, brukerbredder, containerbredde eller
 * innholdslengden endres — ikke ved hver datarefresh — så kolonnene ikke hopper.
 */

import { useLayoutEffect, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDefinition, TableDataRow } from '../types/table';

/**
 * Kolonner som får restplassen: ren fritekst. Tall/dato/status holdes smale, og det samme
 * gjør tekstkolonner med egen renderer (statusmerker, nedtrekkslister, knapper) — innholdet
 * deres har fast bredde, så ekstra plass blir bare tomrom.
 */
const isFlexColumn = <T extends TableDataRow>(col: ColumnDefinition<T>): boolean =>
  (col.type === undefined || col.type === 'text') && col.alignment !== 'right' && !col.renderer;

/** Ekstra piksler per kolonne så avrunding aldri kutter siste tegn. */
const ROUNDING_BUFFER = 2;

export function useAutoFitColumns<T extends TableDataRow>(
  visibleColumns: ColumnDefinition<T>[],
  rows: T[]
) {
  const tableRef = useRef<HTMLTableElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoWidths, setAutoWidths] = useState<Record<string, number>>({});
  const [containerWidth, setContainerWidth] = useState(0);

  // Følg containerbredden (vindu, sidemeny inn/ut). Småendringer ignoreres.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      setContainerWidth(prev => (Math.abs(prev - width) >= 8 ? width : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Innholdssignatur: lengste råverdi per auto-kolonne. Endres bare når innholdet
  // faktisk blir lengre/kortere — ikke ved hver refresh med samme data.
  const contentSignature = useMemo(() => {
    return visibleColumns
      .filter(col => !col.userWidth)
      .map(col => {
        let max = 0;
        for (const row of rows) {
          const value = row[col.key];
          const len = value === null || value === undefined ? 0 : String(value).length;
          if (len > max) max = len;
        }
        return `${col.id}:${max}`;
      })
      .join('|') + `#${rows.length > 0 ? 1 : 0}`;
  }, [visibleColumns, rows]);

  const layoutSignature = useMemo(
    () => visibleColumns.map(col => `${col.id}:${col.userWidth ? col.width : 'a'}:${col.label}`).join('|'),
    [visibleColumns]
  );

  useLayoutEffect(() => {
    const table = tableRef.current;
    const container = containerRef.current;
    if (!table || !container) return;
    const available = container.clientWidth;
    if (available === 0) return; // skjult (fane/modal) — ResizeObserver trigger ny måling

    const autoColumns = visibleColumns.filter(col => !col.userWidth);
    if (autoColumns.length === 0) {
      setAutoWidths(prev => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    // --- 1. Midlertidig auto-layout ---
    const restoreTable = { layout: table.style.tableLayout, width: table.style.width };
    table.style.tableLayout = 'auto';
    table.style.width = 'max-content';

    const sizedCells = Array.from(table.querySelectorAll<HTMLElement>('th[style], td[style]'));
    const savedCellStyles = sizedCells.map(el => ({ el, width: el.style.width, minWidth: el.style.minWidth }));
    sizedCells.forEach(el => { el.style.width = ''; el.style.minWidth = ''; });

    // Rader som ikke sier noe om kolonnebredde: filterrad (input har egen standardbredde)
    // og rader med sammenslåtte celler (detaljrader, «ingen data»).
    const hiddenRows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr')).filter(tr =>
      tr.hasAttribute('data-filter-row') ||
      Array.from(tr.cells).some(cell => cell.colSpan > 1)
    );
    const savedDisplay = hiddenRows.map(tr => ({ tr, display: tr.style.display }));
    hiddenRows.forEach(tr => { tr.style.display = 'none'; });

    // Skjemafelt i cellene: nettleserens naturlige bredde er feil mål. En <select> blir
    // like bred som det LENGSTE valget i lista (f.eks. alle leveringsmåter), og et tekstfelt
    // like bredt som size=20. Mål dem etter verdien som faktisk vises.
    const formFields = Array.from(table.querySelectorAll<HTMLSelectElement | HTMLInputElement>(
      'tbody select, tbody input[type="text"], tbody input[type="number"], tbody input:not([type])'
    ));
    const savedFieldStyles = formFields.map(el => ({ el, width: el.style.width, minWidth: el.style.minWidth, maxWidth: el.style.maxWidth }));
    if (formFields.length > 0) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      formFields.forEach(el => {
        const cs = window.getComputedStyle(el);
        if (cs.display === 'none') return;
        const text = el instanceof HTMLSelectElement
          ? (el.selectedOptions[0]?.text ?? '')
          : (el.value || el.placeholder || '');
        let textWidth = text.length * 7;
        if (ctx) {
          ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
          textWidth = ctx.measureText(text).width;
        }
        const chrome = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
          + parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth)
          // Native pil i <select> ligger utenfor padding når appearance ikke er slått av
          + (el instanceof HTMLSelectElement && cs.appearance !== 'none' ? 20 : 0);
        const width = Math.ceil(Math.max(40, textWidth + chrome + 4));
        el.style.width = `${width}px`;
        el.style.minWidth = `${width}px`;
        el.style.maxWidth = `${width}px`;
      });
    }

    // --- 2. Les naturlige bredder ---
    const natural: Record<string, number> = {};
    let extrasWidth = 0; // utvid/velg/handlinger-kolonner uten data-col-id
    const headerRow = table.tHead?.rows[0];
    if (headerRow) {
      Array.from(headerRow.cells).forEach(cell => {
        const id = cell.getAttribute('data-col-id');
        const width = Math.ceil(cell.getBoundingClientRect().width);
        if (id) natural[id] = width;
        else extrasWidth += width;
      });
    }

    // --- Gjenopprett ---
    savedFieldStyles.forEach(({ el, width, minWidth, maxWidth }) => {
      el.style.width = width; el.style.minWidth = minWidth; el.style.maxWidth = maxWidth;
    });
    savedDisplay.forEach(({ tr, display }) => { tr.style.display = display; });
    savedCellStyles.forEach(({ el, width, minWidth }) => { el.style.width = width; el.style.minWidth = minWidth; });
    table.style.tableLayout = restoreTable.layout;
    table.style.width = restoreTable.width;

    // --- 3. Fordel bredde ---
    const next: Record<string, number> = {};
    let sum = 0;
    for (const col of autoColumns) {
      const measured = (natural[col.id] ?? col.width) + ROUNDING_BUFFER;
      const clamped = Math.max(col.minWidth || 60, col.maxWidth ? Math.min(col.maxWidth, measured) : measured);
      next[col.id] = clamped;
      sum += clamped;
    }
    const userSum = visibleColumns.filter(col => col.userWidth).reduce((acc, col) => acc + col.width, 0);
    // -2: tabellens egen ramme
    const remaining = available - extrasWidth - userSum - sum - 2;

    if (remaining > 0) {
      let receivers = autoColumns.filter(col => isFlexColumn(col) && !col.maxWidth);
      // Ingen fritekstkolonne synlig: gi plassen til siste kolonne (ellers fordeler
      // nettleseren den jevnt over alle kolonner, også tallene).
      if (receivers.length === 0) receivers = [autoColumns[autoColumns.length - 1]];
      const receiverSum = receivers.reduce((acc, col) => acc + next[col.id], 0);
      let given = 0;
      receivers.forEach((col, idx) => {
        const share = idx === receivers.length - 1
          ? remaining - given
          : Math.floor(remaining * (next[col.id] / receiverSum));
        next[col.id] += share;
        given += share;
      });
    }

    setAutoWidths(prev => {
      const prevKeys = Object.keys(prev);
      const same = prevKeys.length === Object.keys(next).length
        && prevKeys.every(key => prev[key] === next[key]);
      return same ? prev : next;
    });
    // visibleColumns/rows leses via signaturene — bevisst ikke i avhengighetslisten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSignature, layoutSignature, containerWidth]);

  /** Synlige kolonner med effektiv bredde (brukerbredde, ellers målt bredde). */
  const displayColumns = useMemo(
    () => visibleColumns.map(col =>
      !col.userWidth && autoWidths[col.id] !== undefined ? { ...col, width: autoWidths[col.id] } : col
    ),
    [visibleColumns, autoWidths]
  );

  return { tableRef, containerRef, displayColumns };
}
